import { useRouter } from 'next/router';
import { getLocale } from '@hub/libs/constants';
import { useTranslation } from 'next-i18next';
import { WidenImage } from '@hub/common/Widen/WidenImage';
import type { ProductTableProps } from '@hub/bearing-hub/BearingHubPage/BearingsTable/BearingsTable';
import { BearingTableDimensions } from '@hub/bearing-hub/BearingHubPage/BearingsTable/BearingTableDimensions';
import { useNextHashQueryParams } from '@hub/hooks/useNextQueryParams';
import { type CartArticleData } from '@hub/bearing-hub/common/basket/useAddToShoppingCart';
import { useEffect, useMemo, useState } from 'react';
import { getArticleName } from '@hub/bearing-hub/common/articleName.factory';
import { Icon, IconColor, IconID } from '@hub/common/Icon/Icon';
import { DEFAULT_QUANTITY } from '@hub/bearing-hub/common/constants';
import { displayPrice } from '@hub/libs/product-price-utils';
import dynamic from 'next/dynamic';
import { getProcedureText } from '@hub/bearing-hub/common/utils';
import Link from 'next/link';
import { getRedirectLinkToProductDetails } from '@hub/bearing-hub/common/link.factory';
import { type HeaderDefinition } from '@hub/bearing-hub/BearingHubPage/BearingsTable/useBearingsTableStore';
import { IbhEventCategory, TrackedIbhEventActions } from '@hub/bearing-hub/common/tracking.config';
import { usePiwikPro } from '@piwikpro/next-piwik-pro';
import type { IbhIcnPartsResponseDto } from '@igus/iglidur-bearing-hub-api/src/data-contracts';
import { IbhProductionMethods } from '@igus/iglidur-bearing-hub-api/src/data-contracts';
import { Checkbox } from '@hub/common/Checkbox/Checkbox';
import { createNumberFormatter } from '@icommerce/i18n';
import { ProgressIndicator, ProgressShape, ProgressThickness } from '@icommerce/ui';

const Loader = () => {
  const { t } = useTranslation([
    'common',
    'category/categoryPage',
    'bearing-hub/bearingHub',
    'free-form-hub/freeFormHubCommon',
  ]);
  return (
    <div className="flex justify-center items-center py-8">
      <ProgressIndicator
        label={t('COMMON.LOADING', { ns: 'free-form-hub/freeFormHubCommon' })}
        shape={ProgressShape.Circular}
        thickness={ProgressThickness.Thin}
        size={20}
      />
      <span className="ml-3 text-gray-600">{t('COMMON.SELECTION_LOADING', { ns: 'bearing-hub/bearingHub' })}</span>
    </div>
  );
};

const CellLoader = ({ center = false }: { center?: boolean }) => {
  const { t } = useTranslation('free-form-hub/freeFormHubCommon');
  return (
    <div className={`absolute ${center ? 'left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2' : ''}`}>
      <ProgressIndicator
        label={t('COMMON.LOADING')}
        shape={ProgressShape.Circular}
        thickness={ProgressThickness.Thin}
        size={17.5}
      />
    </div>
  );
};

const LifetimeBarLoader = () => (
  <div className="w-full h-[4px] rounded-full bg-gray-200 overflow-hidden mt-1">
    <div className="h-full bg-gradient-to-r from-[#C8DFE9] via-[#2480A8] to-[#C8DFE9] animate-[shimmer_1.5s_infinite]" />
    <style jsx>{`
      @keyframes shimmer {
        0% {
          transform: translateX(-100%);
        }
        100% {
          transform: translateX(100%);
        }
      }
    `}</style>
  </div>
);

const ExpandedRow = dynamic(() => import('./ExpandedRow').then(mod => mod.ExpandedRow), {
  ssr: false,
  loading: () => <Loader />,
});

type BearingTableExpandableRowProps = {
  isExpanded: boolean;
  expandChange: (articleNumber: string, isExpanded: boolean) => void;
  selectPart: (articleNumber: string) => void;
  tableHeaders: HeaderDefinition[];
  selectedParts: string[];
  articlesAddingToBasket: string[];
  addToShoppingCart: (articleData: CartArticleData, articleThumbnail?: string) => void;
  isLifetimeLoading: boolean;
} & ProductTableProps;

export const BearingTableExpandableRow = ({
  data,
  maxLifeTimeReference,
  isCustom = false,
  selectedShape,
  unitSystem,
  clientReady,
  isExpanded,
  expandChange,
  selectPart,
  tableHeaders,
  selectedParts,
  articlesAddingToBasket,
  addToShoppingCart,
  lifetime,
  isLifetimeLoading,
}: BearingTableExpandableRowProps) => {
  const router = useRouter();
  const locale = getLocale(router);
  const { t, i18n } = useTranslation(['common', 'category/categoryPage', 'bearing-hub/bearingHub']);
  const { unitPrice, setupCost, widenShortKey, articleNumber, deliveryTimeInDays, productionMethod, material } = data;
  const { icnProcedure } = data as IbhIcnPartsResponseDto;
  const lifetimeFactor =
    lifetime > 0 && maxLifeTimeReference > 0
      ? Math.min(80, Math.round(Math.sqrt(lifetime / maxLifeTimeReference) * 80))
      : 0;
  const query = useNextHashQueryParams();
  const hashParams = clientReady ? query : {};
  const quantity = useMemo(() => Number(hashParams.quantity) || DEFAULT_QUANTITY, [hashParams.quantity]);
  const { CustomEvent } = usePiwikPro();
  const [addToBasketInProgress, setAddToBasketInProgress] = useState(false);

  const addToBasketButton = () => {
    if (addToBasketInProgress) {
      return <CellLoader />;
    }
    return unitPrice.value > 0 ? (
      <Icon id={IconID.ADD_SHOPPING_CART} size={9} color={clientReady ? IconColor.ORANGE : IconColor.LIGHT_GRAY} />
    ) : (
      <Icon id={IconID.REQUEST_QUOTE} size={9} color={clientReady ? IconColor.ORANGE : IconColor.LIGHT_GRAY} />
    );
  };

  const getArticleNumber = () => {
    const imperialArticleNumber = getArticleName(locale, data);
    return (
      <>
        <p>{isCustom ? t(`CUSTOM_PART`, { ns: 'bearing-hub/bearingHub' }) : data.articleNumber}</p>
        {data.articleNumber !== imperialArticleNumber && (
          <p>
            ({t(`FORMERLY`, { ns: 'bearing-hub/bearingHub' })} {imperialArticleNumber})
          </p>
        )}
      </>
    );
  };

  const onAddToShoppingCart = () => {
    void addToShoppingCart(data);
    CustomEvent.trackEvent(
      IbhEventCategory,
      TrackedIbhEventActions.transferToShoppingBasketResultView,
      JSON.stringify({ article: articleNumber, quantity, price: unitPrice })
    );
  };

  useEffect(() => {
    setAddToBasketInProgress(articlesAddingToBasket.includes(articleNumber));
  }, [articlesAddingToBasket]);

  const getArticleLifeTimeCell = () => {
    if (isLifetimeLoading) {
      return <LifetimeBarLoader />;
    }

    if (lifetime === undefined || Number.isNaN(lifetime)) {
      return <span className="whitespace-nowrap">{t(`LIFETIME.UPON_REQUEST`, { ns: 'bearing-hub/bearingHub' })}</span>;
    }

    const ceilLifeTime = Math.ceil(lifetime);

    return (
      <span className="z-5">
        {ceilLifeTime < 0 ? (
          <span className="whitespace-nowrap">{t(`LIFETIME.UPON_REQUEST`, { ns: 'bearing-hub/bearingHub' })}</span>
        ) : (
          <span className="whitespace-nowrap">{createNumberFormatter(locale).format(ceilLifeTime)} h</span>
        )}
        {ceilLifeTime >= 0 && (
          <div className="left-1 rounded-full h-[4px] mt-1 bg-[#C8DFE9]">
            <div className="left-0 rounded-full h-[4px] bg-[#2480A8]" style={{ width: `${lifetimeFactor}%` }} />
          </div>
        )}
      </span>
    );
  };

  const articleImage = (
    <td className="relative h-[100%] top-0 left-0 z-20 group-hover:bg-[#FDF2E5] transition duration-200 px-2 xl:px-3">
      <div className="flex items-center h-full justify-center">
        {widenShortKey ? (
          <div className="flex justify-items-start justify-center align-middle">
            <div className="w-full flex justify-center">
              <WidenImage
                shortCode={widenShortKey}
                title={articleNumber}
                filename={widenShortKey}
                width={64}
                height={64}
                mixBlendMode
              />
            </div>
          </div>
        ) : (
          <div className="flex justify-items-start justify-center align-middle">
            <WidenImage
              shortCode={selectedShape.widenShortKeyIcon}
              title={articleNumber}
              filename={selectedShape.widenShortKeyIcon}
              width={64}
              height={64}
            />
          </div>
        )}
      </div>
    </td>
  );

  const articleData = (
    <td className="relative z-20 px-2 xl:px-3" data-testid="table-cell-material">
      <span className="font-bold z-5" data-testid="table_cell_material_name">
        {material.name.replace(/-CNC/g, '')}
      </span>
      <div className="text-igus-primary" data-testid="table-cell-article-number">
        <Link
          href={getRedirectLinkToProductDetails(data, selectedShape, unitSystem, hashParams, isCustom, locale)}
          onClick={e => {
            e.stopPropagation();
          }}
        >
          {getArticleNumber()}
        </Link>
      </div>
      <p className="z-5 whitespace-normal" data-testid="table_cell_production_method">
        {getProcedureText(
          productionMethod,
          t,
          productionMethod === IbhProductionMethods.SEMIFINISHED ? icnProcedure : material.procedure
        )}
      </p>
    </td>
  );

  const articleDimensions = (
    <td className="xl:table-cell relative px-2 xl:px-3" data-testid="table-cell-dimensions">
      <div className="w-[100%] absolute h-[100%] top-0 left-0 z-10" />
      <div className="flex flex-wrap z-5">
        {data?.dimensions ? (
          <BearingTableDimensions selectedShape={selectedShape} data={data} locale={locale} unitSystem={unitSystem} />
        ) : null}
      </div>
    </td>
  );

  const articleDeliveryInfo = (
    <td
      className="pr-4 pl-4 relative hidden xl:table-cell px-2 xl:px-3 w-[80px]"
      data-testid="table-cell-delivery-time"
    >
      <div className="w-[100%] absolute h-[100%] top-0 left-0 z-10" />
      {deliveryTimeInDays && !Number.isNaN(deliveryTimeInDays) && (
        <span className="z-5">
          {deliveryTimeInDays < 0 ? (
            <span>{t(`DELIVERY_TIMES.UPON_REQUEST`, { ns: 'bearing-hub/bearingHub' })}</span>
          ) : (
            <span className="whitespace-nowrap">
              {deliveryTimeInDays} {t(`HEADLINES.DAYS`, { count: deliveryTimeInDays, ns: 'bearing-hub/bearingHub' })}
            </span>
          )}
        </span>
      )}
    </td>
  );

  const articleLifetimeAndDeliveryInfo = (
    <td
      className="min-w-[100px] relative table-cell xl:hidden px-2 xl:px-3"
      data-testid="table-cell-delivery-time"
      colSpan={2}
    >
      <div className="w-[100%] absolute h-[100%] top-0 left-0 z-10" />
      {deliveryTimeInDays && !Number.isNaN(deliveryTimeInDays) && (
        <span className="z-5">
          {deliveryTimeInDays < 0 ? (
            <span className="whitespace-nowrap">
              {t(`DELIVERY_TIMES.UPON_REQUEST`, { ns: 'bearing-hub/bearingHub' })}
            </span>
          ) : (
            <span className="whitespace-nowrap">
              {deliveryTimeInDays} {t(`HEADLINES.DAYS`, { count: deliveryTimeInDays, ns: 'bearing-hub/bearingHub' })}
            </span>
          )}
        </span>
      )}{' '}
      /
      <br />
      {getArticleLifeTimeCell()}
    </td>
  );

  const articleLifetimeInfo = (
    <td className="relative text-center pr-4 hidden xl:table-cell px-2 xl:px-3" data-testid="table-cell-lifetime">
      <div className="w-[100%] absolute h-[100%] top-0 left-0 z-10" />
      {getArticleLifeTimeCell()}
    </td>
  );

  const priceInfo = (
    <td className="font-bold text-right relative px-2 xl:px-3" data-testid="table-cell-price">
      {unitPrice.value > 0 ? (
        <div className="z-5" data-testid="table_unit_price">
          {displayPrice(unitPrice.value, unitPrice.unit, i18n.language)}
          {setupCost.value > 0 && (
            <>
              <span className="text-igus-primary"> *</span>
              <p className="text-xxs mt-1 text-gray-300">
                {t(`PRICE_PLUS_SETUP_COSTS_SHORT`, {
                  price: displayPrice(setupCost.value, setupCost.unit, i18n.language),
                  ns: 'bearing-hub/bearingHub',
                })}{' '}
                <span className="text-igus-primary">*</span>
              </p>
            </>
          )}
        </div>
      ) : (
        <span>{t(`BUY_BOX.PRICE_ON_REQUEST`, { ns: 'product/buyBox' })}</span>
      )}
      <div className="w-full h-12 relative">
        <button
          type="button"
          disabled={addToBasketInProgress || !clientReady}
          className={`w-12 h-12 flex items-center justify-center absolute top-0 -right-2 rounded-full hover:bg-black/[0.04] ${
            addToBasketInProgress || !clientReady ? 'cursor-wait' : 'cursor-pointer'
          } z-30`}
          onClick={event => {
            event.stopPropagation();
            void onAddToShoppingCart();
          }}
        >
          {addToBasketButton()}
        </button>
      </div>
    </td>
  );

  const selection = tableHeaders.find(header => header.key === 'selection') && (
    <td className="px-2 xl:px-3" data-testid="table-cell-selection">
      <div
        className="relative h-full flex justify-end items-center top-[-5px]"
        onClick={event => event.stopPropagation()}
      >
        <Checkbox
          id={`selection_${articleNumber}`}
          value={selectedParts.includes(articleNumber)}
          checkBoxClassName="h-[18px!important] w-[18px!important] after:h-[11px!important] after:w-[7px!important] after:left-[6px!important] after:top=[2px!important]"
          onChange={() => selectPart(articleNumber)}
        />
      </div>
    </td>
  );

  return (
    <>
      <tr
        data-testid="product-table-row"
        className="h-[80px] text-left border border-[#AFAFAF] text-sm relative z-10 hover:bg-[#FDF2E5] group transition duration-200"
        onClick={() => {
          expandChange(articleNumber, !isExpanded);
        }}
      >
        {articleImage}
        {articleData}
        {articleDimensions}
        {articleDeliveryInfo}
        {articleLifetimeAndDeliveryInfo} {/* xl:hidden */}
        {articleLifetimeInfo} {/* hidden xl:table-cell */}
        {priceInfo}
        {selection}
      </tr>
      {isExpanded && (
        <tr>
          <td colSpan={7} data-testid="table-expanded-row">
            <div className="text-left transition-[max-height] my-8 duration-[700ms] ease-in-out">
              <ExpandedRow
                articleData={data}
                shape={selectedShape}
                isCustom={isCustom}
                quantity={quantity}
                addToShoppingCart={addToShoppingCart}
                clientReady={clientReady}
                unitSystem={unitSystem}
                addToBasketInProgress={addToBasketInProgress}
              />
            </div>
          </td>
        </tr>
      )}
    </>
  );
};
